import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { useToast } from "../hooks/use-toast";
import api, { errMsg } from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

function EmailPrefCard() {
  const { toast } = useToast();
  const [on, setOn] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/me/preferences").then((r) => setOn(r.data.email_notifications)).catch(() => setOn(true)); }, []);
  const change = async (v) => {
    setSaving(true);
    try {
      const r = await api.put("/me/preferences", { email_notifications: v });
      setOn(r.data.email_notifications);
      toast({ title: v ? "Email notifications on" : "Email notifications off" });
    } catch (err) {
      toast({ title: "Could not save preference", description: errMsg(err), variant: "destructive" });
    } finally { setSaving(false); }
  };
  return (
    <Card>
      <h2 className="text-base font-bold text-slate-900">Notifications</h2>
      <p className="text-sm text-slate-500">In-app notifications (the bell icon) are always on. Choose whether you also get emails.</p>
      <div className="mt-2 flex items-center justify-between py-3">
        <div>
          <div className="text-sm font-medium text-slate-800">Email notifications</div>
          <div className="text-xs text-slate-500">Status updates for equipment, training, assignments and support requests</div>
        </div>
        <Switch checked={!!on} disabled={on === null || saving} onCheckedChange={change} data-testid="email-notifications-toggle" />
      </div>
    </Card>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) {
      toast({ title: "Weak password", description: "Use at least 8 characters.", variant: "destructive" });
      return;
    }
    if (pw.new_password !== pw.confirm) {
      toast({ title: "Mismatch", description: "New passwords do not match.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast({ title: "Password updated", description: "Your password was changed successfully." });
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Could not update password", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <EmailPrefCard />

      <Card>
        <h2 className="text-base font-bold text-slate-900">Change password</h2>
        <form onSubmit={submitPw} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required />
          </div>
          <Separator />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="btn-primary-gradient border-0 text-white">
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
